import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LLMConnection, LLMConnectionModel } from '#/llm'
import { AIServiceType, aiProviderFactory, IAIProvider } from '@/ai'
import { useCallback } from 'react'

interface LLMConnectionState {
  // 配置数据
  connections: LLMConnection[],
  // 加载模型中
  loadingModels: string[],
  connectionModels: {
    [key in AIServiceType]: LLMConnectionModel[]
  }

  // 操作方法
  addConnection: (connection: LLMConnection) => void
  updateConnection: (connection: LLMConnection) => void
  removeConnection: (connection: LLMConnection) => boolean
  getConnection: (id?: string) => LLMConnection | undefined
  getAllConnections: () => LLMConnection[],
  refreshModel: (connection: LLMConnection) => Promise<void>
  refreshAllModels: () => Promise<void>
  getAllModels: () => LLMConnectionModel[]
}

export const useLLMConnectionStore = create<LLMConnectionState>()(
  persist(
    (set, get) => ({
      // 初始状态
      connections: [],
      loadingModels: [],
      connectionModels: {
        [AIServiceType.OpenAI]: [],
        [AIServiceType.DeepSeek]: [],
        [AIServiceType.Ollama]: [],
        [AIServiceType.OpenRouter]: [],
        [AIServiceType.OpenPipe]: [],
      },

      // 配置操作方法
      addConnection: (connection: LLMConnection) => {
        set((state) => {
          const newConnections = [...state.connections, connection]
          return { connections: newConnections }
        })
      },

      updateConnection: (connection: LLMConnection) => {
        set((state) => {
          const newConnections = state.connections.map(c => c.id === connection.id ? connection : c)
          return { connections: newConnections }
        })
      },

      removeConnection: (connection: LLMConnection) => {
        set((state) => {
          const newConnections = state.connections.filter(c => c.id !== connection.id)
          return { connections: newConnections }
        })
        return true
      },

      getConnection: (id?: string) => {
        return get().connections.find(c => c.id === id)
      },

      getAllConnections: () => {
        return get().connections
      },

      refreshModel: async (connection: LLMConnection) => {
        if (connection.disabled) {
          set({
            connectionModels: {
              ...get().connectionModels,
              [connection.provider]: []
            }
          })
          return
        }
        // 论文台代付连接：不打上游 /models，由 ensureThesisPlatformConnection 注入
        if (connection.id === 'thesis-platform') return
        if (get().loadingModels.includes(connection.id)) return
        set({ loadingModels: get().loadingModels.filter(id => id !== connection.id).concat(connection.id) })
        const provider = aiProviderFactory.createProvider({
          aiServiceType: connection.provider,
          apiKey: connection.apiKey,
          baseUrl: connection.baseUrl,
        })
        const models = await provider.getAvailableModels().catch(() => [])
        set({
          connectionModels: {
            ...get().connectionModels,
            [connection.provider]: models.map(m => ({ ...m, connectionId: connection.id }))
          }
        })
        set({ loadingModels: get().loadingModels.filter(id => id !== connection.id) })
      },

      refreshAllModels: async () => {
        await Promise.all(get().connections.map(c => get().refreshModel(c)))
      },

      getAllModels: () => {
        return Object.values(get().connectionModels).flat()
      }
    }),
    {
      name: 'llm-connection-storage', // 缓存键名
      partialize: (state) => ({
        connections: state.connections,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.connections = state.connections
        }
      },
    }
  )
)

export const useAllModels = () => {
  return useLLMConnectionStore(state => state.getAllModels())
}

export const useConnectionModels = (provider: AIServiceType) => {
  return useLLMConnectionStore(state => state.connectionModels[provider])
}

export const useGetGenerateProviderByModel = (): ((modelId: string) => IAIProvider | undefined) => {
  return useCallback((modelId: string) => {
    const store = useLLMConnectionStore.getState()
    const model = store.getAllModels().find(m => m.name === modelId)
    const connection = store.connections.find(c => c.id === model?.connectionId)
    if (!connection) return undefined
    return aiProviderFactory.createProvider({
      aiServiceType: connection.provider,
      apiKey: connection.apiKey,
      baseUrl: connection.baseUrl,
    })
  }, [])
}
